/**
 * Machine Edge Skills runtime — shared Live client/model (F1).
 * Running/Staged from device telemetry projection only — never capability claimed/verified.
 * Soft≠Live: production never sessionStorage or hardcoded install success.
 * Honesty: HTTP accept is not install success · Never treats HTTP acceptance alone as success
 *          · device_ops_allowed · view-only until device_ops_allowed
 *          · Inventory stale · action disabled
 */
import { path } from "./api.js";

export const EDGE_SKILL_RUNTIME_STATUS_PATH = "/edge/skills/runtime/status";
export const EDGE_SKILL_RUNTIME_ARTIFACTS_PATH = "/edge/skills/runtime/artifacts";

export const EDGE_SKILL_RUNTIME_POLL_MS = 2500;
export const EDGE_SKILL_RUNTIME_POLL_MAX = 24;
export const EDGE_SKILL_RUNTIME_TELEMETRY_POLL_MS = 1500;
export const EDGE_SKILL_RUNTIME_TELEMETRY_POLL_MAX = 8;

export const EDGE_SKILL_RUNTIME_DISPATCHED =
  "Sent to the board · waiting for it to confirm";
export const EDGE_SKILL_RUNTIME_ACK_PENDING =
  "The board has not confirmed yet · wait, then read again";
export const EDGE_SKILL_RUNTIME_ACK_OK_TELEMETRY_PENDING =
  "The board confirmed · skills list is still catching up";
export const EDGE_SKILL_RUNTIME_INSTALL_REFUSAL =
  "This board is not ready to install skills yet";
export const EDGE_SKILL_RUNTIME_NO_HTTP_SUCCESS =
  "The board must confirm before this skill is on";

const RUNTIME_TERMINAL = new Set(["dispatch_failed", "acked_ok", "acked_fail"]);
const RUNTIME_INFLIGHT = new Set(["dispatching", "dispatched"]);

/** Safe contract path with literal fallback when manifest lags. */
export function edgeSkillRuntimeStatusPath() {
  try {
    return path("get__edge_skills_runtime_status");
  } catch {
    return EDGE_SKILL_RUNTIME_STATUS_PATH;
  }
}

export function edgeSkillRuntimeDevicePath(deviceId) {
  const id = String(deviceId || "").trim();
  if (!id) return "";
  try {
    return path("get__edge_skills_runtime_devices_p", { pathParams: [id] });
  } catch {
    return "/edge/skills/runtime/devices/" + encodeURIComponent(id);
  }
}

export function edgeSkillRuntimeDeviceJobsPath(deviceId, opts) {
  const id = String(deviceId || "").trim();
  if (!id) return "";
  const o = opts && typeof opts === "object" ? opts : {};
  const query = {};
  if (Number.isFinite(Number(o.limit))) query.limit = Number(o.limit);
  try {
    return path("get__edge_skills_runtime_devices_p_jobs", { pathParams: [id], query });
  } catch {
    const base = "/edge/skills/runtime/devices/" + encodeURIComponent(id) + "/jobs";
    const parts = Object.entries(query).map(
      ([k, v]) => encodeURIComponent(k) + "=" + encodeURIComponent(String(v)),
    );
    return parts.length ? base + "?" + parts.join("&") : base;
  }
}

export function edgeSkillRuntimeArtifactsPath(opts) {
  const o = opts && typeof opts === "object" ? opts : {};
  const query = {};
  const sid = String(o.skillId || "").trim();
  if (sid) query.skill_id = sid;
  try {
    return path("get__edge_skills_runtime_artifacts", { query });
  } catch {
    const parts = Object.entries(query).map(
      ([k, v]) => encodeURIComponent(k) + "=" + encodeURIComponent(String(v)),
    );
    return EDGE_SKILL_RUNTIME_ARTIFACTS_PATH + (parts.length ? "?" + parts.join("&") : "");
  }
}

export function edgeSkillRuntimeInstallPath(deviceId) {
  const id = String(deviceId || "").trim();
  if (!id) return "";
  try {
    return path("post__edge_skills_runtime_devices_p_install", { pathParams: [id] });
  } catch {
    return "/edge/skills/runtime/devices/" + encodeURIComponent(id) + "/install";
  }
}

export function edgeSkillRuntimeActivatePath(deviceId) {
  const id = String(deviceId || "").trim();
  if (!id) return "";
  try {
    return path("post__edge_skills_runtime_devices_p_activate", { pathParams: [id] });
  } catch {
    return "/edge/skills/runtime/devices/" + encodeURIComponent(id) + "/activate";
  }
}

export function edgeSkillRuntimeRemovePath(deviceId) {
  const id = String(deviceId || "").trim();
  if (!id) return "";
  try {
    return path("post__edge_skills_runtime_devices_p_remove", { pathParams: [id] });
  } catch {
    return "/edge/skills/runtime/devices/" + encodeURIComponent(id) + "/remove";
  }
}

function skillOf(raw) {
  if (!raw || typeof raw !== "object") return null;
  const skillId = String(raw.skill_id || "").trim();
  if (!skillId) return null;
  return {
    skillId,
    runtimeState: String(raw.runtime_state || "").trim(),
    deviceAckObserved: raw.device_ack_observed === true,
    intentSource: String(raw.intent_source || "").trim(),
    artifactId: String(raw.artifact_id || "").trim(),
    sha256Hex: String(raw.sha256_hex || "").trim(),
    telemetryObservedAt: String(raw.telemetry_observed_at || "").trim(),
    lastJobId: String(raw.last_job_id || "").trim(),
    updatedAt: String(raw.updated_at || "").trim(),
  };
}

function jobOf(raw) {
  if (!raw || typeof raw !== "object") return null;
  const jobId = String(raw.job_id || "").trim();
  if (!jobId) return null;
  return {
    jobId,
    skillId: String(raw.skill_id || "").trim(),
    action: String(raw.action || "").trim(),
    state: String(raw.state || "").trim(),
    deviceAckObserved: raw.device_ack_observed === true,
    ackOk: raw.ack_ok,
    ackDetail: String(raw.ack_detail || "").trim(),
    artifactId: String(raw.artifact_id || "").trim(),
    createdAt: String(raw.created_at || "").trim(),
    updatedAt: String(raw.updated_at || "").trim(),
    source: String(raw.source || "").trim(),
  };
}

function artifactOf(raw) {
  if (!raw || typeof raw !== "object") return null;
  const artifactId = String(raw.artifact_id || "").trim();
  if (!artifactId) return null;
  return {
    artifactId,
    skillId: String(raw.skill_id || "").trim(),
    sha256Hex: String(raw.sha256_hex || "").trim(),
    sizeBytes: Number(raw.size_bytes) || 0,
    enabled: raw.enabled !== false,
    createdAt: String(raw.created_at || "").trim(),
    updatedAt: String(raw.updated_at || "").trim(),
  };
}

/** @param {unknown} data GET /edge/skills/runtime/status */
export function edgeSkillRuntimeStatusViewOf(data) {
  const root = data && typeof data === "object" ? data : {};
  const productEnabled = root.product_enabled === true;
  return {
    ok: root.ok === true,
    productEnabled,
    deviceOpsAllowed: root.device_ops_allowed === true,
    catalogRequired: root.catalog_required === true,
    artifactUploadAllowed: root.artifact_upload_allowed === true,
    detail: String(root.detail || "").trim(),
    unavailable: !productEnabled,
    softInventForbidden: root.soft_invent_forbidden !== false,
    sor: String(root.sor || "").trim(),
  };
}

/** @param {unknown} data GET /edge/skills/runtime/devices/{device_id} */
export function edgeSkillRuntimeDeviceViewOf(data) {
  const root = data && typeof data === "object" ? data : {};
  const invRaw = root.inventory && typeof root.inventory === "object" ? root.inventory : null;
  const inventory = invRaw
    ? {
        runtimeAbi: String(invRaw.runtime_abi || "").trim(),
        expectedRuntimeAbi: String(invRaw.expected_runtime_abi || "").trim(),
        compiledHandlers: Array.isArray(invRaw.compiled_handlers)
          ? invRaw.compiled_handlers.map((h) => String(h).trim()).filter(Boolean)
          : [],
        bootId: String(invRaw.boot_id || "").trim(),
        lastSeq: Number(invRaw.last_seq) || 0,
        complete: invRaw.complete === true,
        fresh: invRaw.fresh === true,
        ageSec: Number.isFinite(Number(invRaw.age_sec)) ? Number(invRaw.age_sec) : null,
        reason: String(invRaw.reason || "").trim(),
        observedAt: String(invRaw.observed_at || "").trim(),
        source: String(invRaw.source || "").trim(),
      }
    : null;
  const skills = Array.isArray(root.skills)
    ? root.skills.map(skillOf).filter(Boolean)
    : [];
  const observedSkills = Array.isArray(root.observed_skills)
    ? root.observed_skills.map(skillOf).filter(Boolean)
    : skills.filter((s) => s.intentSource === "device_telemetry" && s.deviceAckObserved);
  return {
    deviceId: String(root.device_id || "").trim(),
    deviceOpsAllowed: root.device_ops_allowed === true,
    inventory,
    skills,
    observedSkills,
    count: Number.isFinite(Number(root.count)) ? Number(root.count) : skills.length,
    honesty: String(root.honesty || "").trim(),
  };
}

/** @param {unknown} data GET /edge/skills/runtime/devices/{device_id}/jobs */
export function edgeSkillRuntimeJobsViewOf(data) {
  const root = data && typeof data === "object" ? data : {};
  const jobs = Array.isArray(root.jobs) ? root.jobs.map(jobOf).filter(Boolean) : [];
  return {
    deviceId: String(root.device_id || "").trim(),
    jobs,
    count: Number.isFinite(Number(root.count)) ? Number(root.count) : jobs.length,
  };
}

/** @param {unknown} data GET /edge/skills/runtime/artifacts */
export function edgeSkillRuntimeArtifactsViewOf(data) {
  const root = data && typeof data === "object" ? data : {};
  const items = Array.isArray(root.items) ? root.items.map(artifactOf).filter(Boolean) : [];
  return {
    items,
    count: Number.isFinite(Number(root.count)) ? Number(root.count) : items.length,
    honesty: String(root.honesty || "").trim(),
  };
}

export function edgeSkillRuntimeJobTerminal(state) {
  return RUNTIME_TERMINAL.has(String(state || "").trim());
}

export function edgeSkillRuntimeJobInflight(state) {
  return RUNTIME_INFLIGHT.has(String(state || "").trim());
}

export function edgeSkillRuntimeJobFromDispatchResponse(data) {
  const root = data && typeof data === "object" ? data : {};
  return jobOf(root.job);
}

export function edgeSkillRuntimeJobById(jobsView, jobId) {
  const id = String(jobId || "").trim();
  if (!id) return null;
  return (jobsView && jobsView.jobs ? jobsView.jobs : []).find((j) => j.jobId === id) || null;
}

export function edgeSkillRuntimeJobOutcome(job) {
  const state = job ? String(job.state || "").trim() : "";
  return {
    terminal: edgeSkillRuntimeJobTerminal(state),
    success: state === "acked_ok",
    failed: state === "acked_fail" || state === "dispatch_failed",
    state,
  };
}

export function edgeSkillRuntimePendingJobsOf(jobsView) {
  return (jobsView && jobsView.jobs ? jobsView.jobs : []).filter(
    (j) => edgeSkillRuntimeJobInflight(j.state),
  );
}

export function edgeSkillRuntimeFailedJobsOf(jobsView) {
  return (jobsView && jobsView.jobs ? jobsView.jobs : []).filter((j) => {
    const st = String(j.state || "");
    return st === "acked_fail" || st === "dispatch_failed";
  });
}

export function edgeSkillRuntimeTelemetryObservedForSkill(runtimeView, skillId, expectedStates) {
  const sid = String(skillId || "").trim();
  if (!sid) return false;
  const states = Array.isArray(expectedStates) && expectedStates.length
    ? expectedStates
    : ["active", "staged"];
  const observed = runtimeView && Array.isArray(runtimeView.observedSkills)
    ? runtimeView.observedSkills
    : [];
  return observed.some(
    (s) => s.skillId === sid && s.deviceAckObserved && states.includes(s.runtimeState),
  );
}

export function edgeSkillRuntimeAgeSay(ageSec) {
  if (ageSec == null || !Number.isFinite(Number(ageSec))) return "age unknown";
  const n = Math.max(0, Math.round(Number(ageSec)));
  if (n < 60) return n + "s ago";
  if (n < 3600) return Math.round(n / 60) + "m ago";
  return Math.round(n / 3600) + "h ago";
}

export function edgeSkillRuntimeInventorySay(inventory) {
  const inv = inventory && typeof inventory === "object" ? inventory : null;
  if (!inv) return "Inventory not observed";
  const parts = [];
  if (inv.fresh && inv.complete) parts.push("fresh · complete");
  else if (inv.fresh) parts.push("fresh · incomplete");
  else if (inv.complete) parts.push("stale · complete");
  else parts.push("stale · incomplete");
  if (inv.runtimeAbi) parts.push("ABI " + inv.runtimeAbi);
  if (inv.ageSec != null) parts.push("observed " + edgeSkillRuntimeAgeSay(inv.ageSec));
  if (inv.reason) parts.push(inv.reason);
  return parts.join(" · ");
}

export function edgeSkillRuntimeArtifactForSkill(artifactsView, skillId) {
  const sid = String(skillId || "").trim();
  const items = (artifactsView && artifactsView.items ? artifactsView.items : [])
    .filter((a) => a.skillId === sid && a.enabled !== false);
  if (!items.length) return null;
  return items.sort((a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")))[0];
}

/**
 * Inventory + handler gate shared by install/activate/remove.
 * Requires product enabled and device-specific device_ops_allowed.
 */
export function edgeSkillRuntimeInventoryGateOf(ctx) {
  const c = ctx && typeof ctx === "object" ? ctx : {};
  const status = c.statusView && typeof c.statusView === "object" ? c.statusView : {};
  const runtimeView = c.runtimeView && typeof c.runtimeView === "object" ? c.runtimeView : {};
  const inv = c.inventory && typeof c.inventory === "object"
    ? c.inventory
    : (runtimeView.inventory && typeof runtimeView.inventory === "object" ? runtimeView.inventory : null);
  const skillId = String(c.skillId || "").trim();

  if (!status.productEnabled) {
    return { allowed: false, reason: "product_disabled", label: EDGE_SKILL_RUNTIME_INSTALL_REFUSAL };
  }
  if (runtimeView.deviceOpsAllowed !== true) {
    return {
      allowed: false,
      reason: "device_ops_disabled",
      label: "This seat cannot change skills on this board yet",
    };
  }
  if (!inv) {
    return { allowed: false, reason: "inventory_missing", label: "The board has not reported its skills yet" };
  }
  if (!inv.fresh) {
    return { allowed: false, reason: "inventory_stale", label: "Skills list is stale · wait, then try again" };
  }
  if (!inv.complete) {
    return { allowed: false, reason: "inventory_incomplete", label: "Skills list is incomplete · wait, then try again" };
  }
  if (inv.expectedRuntimeAbi && inv.runtimeAbi && inv.runtimeAbi !== inv.expectedRuntimeAbi) {
    return { allowed: false, reason: "abi_mismatch", label: "Runtime ABI mismatch" };
  }
  const handlers = new Set(inv.compiledHandlers || []);
  if (!skillId || !handlers.has(skillId)) {
    return { allowed: false, reason: "handler_missing", label: "Handler not compiled on device" };
  }
  return { allowed: true, reason: "", label: "Ready" };
}

/** Install gate — inventory gate + compatible catalog row + enabled artifact. */
export function edgeSkillRuntimeInstallGateOf(ctx) {
  const base = edgeSkillRuntimeInventoryGateOf(ctx);
  if (!base.allowed) return base;
  const c = ctx && typeof ctx === "object" ? ctx : {};
  const catalogRow = c.catalogRow && typeof c.catalogRow === "object" ? c.catalogRow : null;
  const artifact = c.artifact && typeof c.artifact === "object" ? c.artifact : null;
  if (!catalogRow || catalogRow.enabled === false) {
    return { allowed: false, reason: "catalog_unavailable", label: "No compatible catalog row" };
  }
  if (!artifact || artifact.enabled === false) {
    return { allowed: false, reason: "artifact_missing", label: "No enabled artifact" };
  }
  return { allowed: true, reason: "", label: "Ready · runtime install" };
}

/** Activate gate — fresh complete inventory + compiled handler + device ops. */
export function edgeSkillRuntimeActivateGateOf(ctx) {
  return edgeSkillRuntimeInventoryGateOf(ctx);
}

/** Remove gate — same inventory/handler requirements as activate. */
export function edgeSkillRuntimeRemoveGateOf(ctx) {
  return edgeSkillRuntimeInventoryGateOf(ctx);
}

/** @param {"install"|"activate"|"remove"} action */
export function edgeSkillRuntimeActionGateOf(action, ctx) {
  const a = String(action || "").trim();
  if (a === "install") return edgeSkillRuntimeInstallGateOf(ctx);
  if (a === "activate") return edgeSkillRuntimeActivateGateOf(ctx);
  if (a === "remove") return edgeSkillRuntimeRemoveGateOf(ctx);
  return { allowed: false, reason: "unknown_action", label: "Unknown runtime action" };
}

/** Product + device-specific ops armed (device view must report device_ops_allowed). */
export function edgeSkillRuntimeOpsAllowed(statusView, runtimeView) {
  const s = statusView && typeof statusView === "object" ? statusView : {};
  const d = runtimeView && typeof runtimeView === "object" ? runtimeView : {};
  return s.productEnabled === true && d.deviceOpsAllowed === true;
}

/** View-only catalog when runtime product or device ops not armed. */
export function edgeSkillRuntimeViewOnly(statusView, runtimeView) {
  return !edgeSkillRuntimeOpsAllowed(statusView, runtimeView);
}

/**
 * Running/Staged rails from telemetry projection only.
 * @param {{
 *   runtimeView?: object,
 *   catalogRows?: object[],
 *   jobsView?: object,
 * }} ctx
 */
export function edgeSkillRuntimeRailsOf(ctx) {
  const c = ctx && typeof ctx === "object" ? ctx : {};
  const runtimeView = c.runtimeView && typeof c.runtimeView === "object" ? c.runtimeView : {};
  const catalogRows = Array.isArray(c.catalogRows) ? c.catalogRows : [];
  const jobsView = c.jobsView && typeof c.jobsView === "object" ? c.jobsView : { jobs: [] };

  const observed = Array.isArray(runtimeView.observedSkills) ? runtimeView.observedSkills : [];
  const allSkills = Array.isArray(runtimeView.skills) ? runtimeView.skills : [];
  const inventory = runtimeView.inventory || null;

  const running = observed.filter(
    (s) => s.runtimeState === "active" && s.deviceAckObserved,
  );
  const staged = observed.filter(
    (s) => s.runtimeState === "staged" && s.deviceAckObserved,
  );
  const onDeviceIds = new Set([
    ...running.map((s) => s.skillId),
    ...staged.map((s) => s.skillId),
  ]);

  const pendingSkills = allSkills.filter(
    (s) => !s.deviceAckObserved && s.intentSource === "registry_intent",
  );
  const pendingJobs = edgeSkillRuntimePendingJobsOf(jobsView);
  const failedJobs = edgeSkillRuntimeFailedJobsOf(jobsView);

  const market = catalogRows.filter((row) => {
    const id = String(row.skillId || row.id || "").trim();
    return id && !onDeviceIds.has(id);
  });

  return {
    running,
    staged,
    market,
    pendingSkills,
    pendingJobs,
    failedJobs,
    inventory,
  };
}

/** Idempotency-Key for one deliberate operator click; reuse for same pending action retry. */
export function edgeSkillRuntimeNewIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "idem-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

/** @param {string} action install|activate|remove */
export function edgeSkillRuntimePendingActionKey(action, skillId, artifactId) {
  const a = String(action || "").trim();
  const sid = String(skillId || "").trim();
  const aid = String(artifactId || "").trim();
  return [a, sid, aid].filter(Boolean).join(":");
}

/** Acquire or reuse pending idempotency key. */
export function edgeSkillRuntimeIdempotencyAcquire(store, pendingKey) {
  const s = store && typeof store === "object" ? store : {};
  if (s[pendingKey]) return s[pendingKey];
  const key = edgeSkillRuntimeNewIdempotencyKey();
  s[pendingKey] = key;
  return key;
}

/**
 * Clear idempotency key only after terminal success or terminal failure.
 * Keep during timeout / inflight so retry reuses the same pending command.
 */
export function edgeSkillRuntimeIdempotencyRelease(store, pendingKey, outcome) {
  const s = store && typeof store === "object" ? store : {};
  const o = outcome && typeof outcome === "object" ? outcome : {};
  if (o.terminal && (o.success || o.failed)) {
    delete s[pendingKey];
  }
}

/**
 * Bounded poll helper — tick returns true when polling should stop.
 * @param {{
 *   tick: function(): Promise<boolean>,
 *   intervalMs?: number,
 *   maxAttempts?: number,
 * }} opts
 */
export async function edgeSkillRuntimePollUntilDone(opts) {
  const o = opts && typeof opts === "object" ? opts : {};
  const tick = typeof o.tick === "function" ? o.tick : async () => true;
  const intervalMs = Number.isFinite(Number(o.intervalMs))
    ? Number(o.intervalMs)
    : EDGE_SKILL_RUNTIME_POLL_MS;
  const maxAttempts = Number.isFinite(Number(o.maxAttempts))
    ? Number(o.maxAttempts)
    : EDGE_SKILL_RUNTIME_POLL_MAX;
  for (let i = 0; i < maxAttempts; i += 1) {
    const stop = await tick();
    if (stop) return { done: true, attempts: i + 1, timedOut: false };
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return { done: false, attempts: maxAttempts, timedOut: true };
}

/**
 * Poll one job_id until terminal or timeout.
 * @param {{
 *   jobId: string,
 *   readJobs: function(): Promise<unknown>,
 *   intervalMs?: number,
 *   maxAttempts?: number,
 *   shouldAbort?: function(): boolean,
 * }} opts
 */
export async function edgeSkillRuntimePollJobUntilTerminal(opts) {
  const o = opts && typeof opts === "object" ? opts : {};
  const jobId = String(o.jobId || "").trim();
  const readJobs = typeof o.readJobs === "function" ? o.readJobs : async () => null;
  const shouldAbort = typeof o.shouldAbort === "function" ? o.shouldAbort : () => false;
  let current = null;
  const poll = await edgeSkillRuntimePollUntilDone({
    intervalMs: o.intervalMs,
    maxAttempts: o.maxAttempts,
    tick: async () => {
      if (shouldAbort()) return true;
      const data = await readJobs();
      if (!data) return false;
      const view = edgeSkillRuntimeJobsViewOf(data);
      current = edgeSkillRuntimeJobById(view, jobId);
      if (!current) return false;
      return edgeSkillRuntimeJobTerminal(current.state);
    },
  });
  const outcome = edgeSkillRuntimeJobOutcome(current);
  return {
    done: poll.done && outcome.terminal,
    timedOut: poll.timedOut || !outcome.terminal,
    attempts: poll.attempts,
    job: current,
    ...outcome,
  };
}

/**
 * After acked_ok, bounded refresh until skill appears in device telemetry.
 * @param {{
 *   skillId: string,
 *   readRuntime: function(): Promise<unknown>,
 *   expectedStates?: string[],
 *   intervalMs?: number,
 *   maxAttempts?: number,
 *   shouldAbort?: function(): boolean,
 * }} opts
 */
export async function edgeSkillRuntimeWaitSkillTelemetry(opts) {
  const o = opts && typeof opts === "object" ? opts : {};
  const skillId = String(o.skillId || "").trim();
  const readRuntime = typeof o.readRuntime === "function" ? o.readRuntime : async () => null;
  const shouldAbort = typeof o.shouldAbort === "function" ? o.shouldAbort : () => false;
  const expectedStates = Array.isArray(o.expectedStates) && o.expectedStates.length
    ? o.expectedStates
    : ["active", "staged"];
  let observed = false;
  const poll = await edgeSkillRuntimePollUntilDone({
    intervalMs: o.intervalMs != null ? o.intervalMs : EDGE_SKILL_RUNTIME_TELEMETRY_POLL_MS,
    maxAttempts: o.maxAttempts != null ? o.maxAttempts : EDGE_SKILL_RUNTIME_TELEMETRY_POLL_MAX,
    tick: async () => {
      if (shouldAbort()) return true;
      const data = await readRuntime();
      if (!data) return false;
      const view = edgeSkillRuntimeDeviceViewOf(data);
      observed = edgeSkillRuntimeTelemetryObservedForSkill(view, skillId, expectedStates);
      return observed;
    },
  });
  return {
    observed,
    timedOut: poll.timedOut && !observed,
    attempts: poll.attempts,
  };
}

/**
 * Dispatch one runtime command, poll returned job_id, optional telemetry wait.
 * Never treats HTTP acceptance alone as success.
 * @param {{
 *   action: string,
 *   skillId: string,
 *   artifactId?: string,
 *   purge?: boolean,
 *   idempotencyStore: object,
 *   dispatch: function({url:string, headers:object, body:object}): Promise<unknown>,
 *   readJobs: function(): Promise<unknown>,
 *   readRuntime: function(): Promise<unknown>,
 *   expectedTelemetryStates?: string[],
 *   skipTelemetry?: boolean,
 * }} opts
 */
export async function edgeSkillRuntimeRunCommand(opts) {
  const o = opts && typeof opts === "object" ? opts : {};
  const action = String(o.action || "").trim();
  const skillId = String(o.skillId || "").trim();
  const artifactId = String(o.artifactId || "").trim();
  const pendingKey = edgeSkillRuntimePendingActionKey(action, skillId, artifactId);
  const idem = edgeSkillRuntimeIdempotencyAcquire(o.idempotencyStore, pendingKey);
  const dispatch = typeof o.dispatch === "function" ? o.dispatch : async () => null;
  const readJobs = typeof o.readJobs === "function" ? o.readJobs : async () => null;
  const readRuntime = typeof o.readRuntime === "function" ? o.readRuntime : async () => null;

  let url = "";
  let body = { skill_id: skillId };
  if (action === "install") {
    body = { skill_id: skillId, artifact_id: artifactId, activate: true };
  } else if (action === "remove") {
    body = { skill_id: skillId, purge: o.purge === true };
  }

  const httpOut = await dispatch({
    url,
    headers: { "Idempotency-Key": idem },
    body,
    action,
    skillId,
    artifactId,
  });
  if (!httpOut) {
    return {
      ok: false,
      phase: "dispatch",
      httpAccepted: false,
      job: null,
      telemetryObserved: false,
      say: "Runtime command refused",
    };
  }

  const job = edgeSkillRuntimeJobFromDispatchResponse(httpOut);
  if (!job || !job.jobId) {
    return {
      ok: false,
      phase: "dispatch",
      httpAccepted: true,
      job: null,
      telemetryObserved: false,
      say: "Sent, but the board did not start a job",
    };
  }

  const poll = await edgeSkillRuntimePollJobUntilTerminal({
    jobId: job.jobId,
    readJobs,
    intervalMs: o.intervalMs,
    maxAttempts: o.maxAttempts,
    shouldAbort: o.shouldAbort,
  });

  if (poll.timedOut || !poll.terminal) {
    return {
      ok: false,
      phase: "poll",
      httpAccepted: true,
      timedOut: poll.timedOut,
      job: poll.job,
      telemetryObserved: false,
      say: EDGE_SKILL_RUNTIME_ACK_PENDING,
    };
  }

  if (poll.failed) {
    edgeSkillRuntimeIdempotencyRelease(o.idempotencyStore, pendingKey, poll);
    const detail = poll.job && poll.job.ackDetail ? (" · " + poll.job.ackDetail) : "";
    return {
      ok: false,
      phase: "terminal",
      httpAccepted: true,
      failed: true,
      job: poll.job,
      telemetryObserved: false,
      say: "Job failed · " + (poll.state || "failed") + detail,
    };
  }

  if (!poll.success) {
    edgeSkillRuntimeIdempotencyRelease(o.idempotencyStore, pendingKey, poll);
    return {
      ok: false,
      phase: "terminal",
      httpAccepted: true,
      job: poll.job,
      telemetryObserved: false,
      say: "Unexpected job terminal state · " + (poll.state || "unknown"),
    };
  }

  if (o.skipTelemetry === true || action === "remove") {
    edgeSkillRuntimeIdempotencyRelease(o.idempotencyStore, pendingKey, poll);
    return {
      ok: true,
      phase: "complete",
      httpAccepted: true,
      job: poll.job,
      telemetryObserved: false,
      say: action === "remove" ? "Remove ACK ok" : "Command ACK ok",
    };
  }

  const telem = await edgeSkillRuntimeWaitSkillTelemetry({
    skillId,
    readRuntime,
    expectedStates: o.expectedTelemetryStates,
    shouldAbort: o.shouldAbort,
  });
  edgeSkillRuntimeIdempotencyRelease(o.idempotencyStore, pendingKey, poll);

  if (!telem.observed) {
    return {
      ok: true,
      phase: "telemetry_pending",
      httpAccepted: true,
      job: poll.job,
      telemetryObserved: false,
      say: EDGE_SKILL_RUNTIME_ACK_OK_TELEMETRY_PENDING,
    };
  }

  return {
    ok: true,
    phase: "complete",
    httpAccepted: true,
    job: poll.job,
    telemetryObserved: true,
    say: "Device telemetry observed",
  };
}

/**
 * Sequential multi-skill pack — one inflight job per device at a time.
 * Stops on first failure, timeout, or gate refusal; never counts HTTP alone as success.
 * @param {{
 *   skills: Array<{skillId:string, artifactId?:string, label?:string}>,
 *   runOne: function(skill): Promise<object>,
 * }} opts
 */
export async function edgeSkillRuntimeRunPackSequential(opts) {
  const o = opts && typeof opts === "object" ? opts : {};
  const skills = Array.isArray(o.skills) ? o.skills : [];
  const runOne = typeof o.runOne === "function" ? o.runOne : async () => ({ ok: false });
  const completed = [];
  for (const sk of skills) {
    const result = await runOne(sk);
    if (!result || result.ok !== true) {
      return {
        ok: false,
        completed,
        stoppedAt: sk,
        result: result || { ok: false, say: "Unknown failure" },
        say: String((sk && (sk.label || sk.skillId)) || "skill")
          + " · "
          + String((result && result.say) || "pack stopped"),
      };
    }
    completed.push({ skill: sk, result });
  }
  return { ok: true, completed, say: "Pack complete · " + completed.length + " skill(s)" };
}
